import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { api } from '../services/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { CheckCircle, Loader2, XCircle, Activity, Heart } from 'lucide-react';

const DonationSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { updateUser } = useAuth();
  const [status, setStatus] = useState('checking');
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (sessionId) {
      pollDonationStatus();
    }
  }, [sessionId]);

  const pollDonationStatus = async (attempts = 0) => {
    const maxAttempts = 5;
    
    if (attempts >= maxAttempts) {
      setStatus('failed');
      return;
    }

    try {
      const response = await api.getDonationStatus(sessionId);
      
      if (response.data.payment_status === 'paid') {
        setStatus('success');
        return;
      }
      
      setTimeout(() => pollDonationStatus(attempts + 1), 2000);
    } catch (error) {
      console.error('Donation status error:', error);
      setTimeout(() => pollDonationStatus(attempts + 1), 2000);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-b from-slate-50 to-white">
      <Card className="w-full max-w-md shadow-lg border-slate-200" data-testid="donation-status-card">
        <CardContent className="p-8 text-center">
          {status === 'checking' && (
            <>
              <div className="w-16 h-16 rounded-full bg-sky-100 flex items-center justify-center mx-auto mb-6">
                <Loader2 className="h-8 w-8 text-sky-600 animate-spin" />
              </div>
              <h1 className="text-2xl font-semibold text-slate-900 mb-2">
                Processing Donation
              </h1>
              <p className="text-slate-500">
                Please wait while we confirm your donation...
              </p>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-6">
                <Heart className="h-8 w-8 text-emerald-600" />
              </div>
              <h1 className="text-2xl font-semibold text-slate-900 mb-2">
                Thank You!
              </h1>
              <p className="text-slate-500 mb-6">
                Your donation helps keep NoSpin independent. Your benefits have been applied to your account.
              </p>
              <Button
                onClick={() => navigate('/')}
                className="w-full h-11 rounded-full bg-slate-900 hover:bg-slate-800 text-white font-medium"
                data-testid="go-to-search"
              >
                <Activity className="h-4 w-4 mr-2" />
                Continue Searching
              </Button>
            </>
          )}

          {status === 'failed' && (
            <>
              <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-6">
                <XCircle className="h-8 w-8 text-red-600" />
              </div>
              <h1 className="text-2xl font-semibold text-slate-900 mb-2">
                Something Went Wrong
              </h1>
              <p className="text-slate-500 mb-6">
                We couldn't confirm your donation. Please try again or contact support.
              </p>
              <Button
                onClick={() => navigate('/')}
                className="w-full h-11 rounded-full bg-slate-900 hover:bg-slate-800 text-white font-medium"
                data-testid="try-again"
              >
                Go Back
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default DonationSuccessPage;
